import type { TFunction } from 'i18next';

/**
 * Extract and translate API error message
 * Handles ErrorCode-based responses from backend
 *
 * @param error - The error object from axios/API call
 * @param t - i18next translation function
 * @param fallbackKey - Translation key to use if no error code or message is found
 * @returns Translated error message
 *
 * @example
 * ```typescript
 * import { getApiErrorMessage } from '@/libs/utils/error-utils';
 * import { useTranslation } from 'react-i18next';
 *
 * const { t } = useTranslation();
 * const errorMessage = getApiErrorMessage(error, t, 'common.error');
 * ```
 */
export function getApiErrorMessage(error: any, t: TFunction, fallbackKey = 'common.error'): string {
  const errorData = error?.response?.data;

  // If error has a code, try to translate it
  if (errorData?.code) {
    return t(`errors.${errorData.code}`, {
      message: errorData.message, // For interpolation in translations like {{message}}
      defaultValue: errorData.message, // Fallback to raw API message
    });
  }

  // Use direct message if available
  if (errorData?.message) {
    return errorData.message;
  }

  // Final fallback
  return t(fallbackKey);
}
